import { useApolloClient } from "@apollo/client";
import { MenuButton, MenuItemWithIcon, ModalOpenButton, theme } from "@msys/ui";
import { ToggleOn as ToggleOnIcon } from "@mui/icons-material";
import { Box, Button, Stack, ThemeProvider } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { SplashScreen } from "../../../common/SplashScreen.js";
import { RestrictedByCapabilityWithDebug } from "../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import { Page } from "../../commons/layout/Page.js";
import { PageColumn } from "../../commons/layout/PageColumn.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PageGrid } from "../../commons/layout/PageGrid.js";
import { BrandingAd } from "../../features/branding/BrandingAd.js";
import { useBranding } from "../../features/branding/context.js";
import { useInvitationListQuery } from "../../features/invitations/Invitations.generated.js";
import { OnboardingFlowModal } from "../../features/organisations/onboarding/OnboardingFlowModal.js";
import { useOnboardingFlow } from "../../features/organisations/onboarding/useOnboardingFlow.js";
import { TodosBox } from "../../features/todos/TodosBox.js";
import { VisibilityEditModal } from "../../features/users/VisibilityEditModal.js";
import { PendingInvitationsBox } from "../../features/users/boxes/PendingInvitationsBox.js";
import { useVisibilityStore } from "../../features/users/useVisibilityStore.js";
import { DashboardAppNotificationsBox } from "./boxes/DashboardAppNotificationsBox.js";
import { DashboardOpportunitiesBox } from "./boxes/DashboardOpportunitesBox.js";
import { DashboardPersonalCalendarBox } from "./boxes/DashboardPersonalCalendarBox.js";
import { DashboardPersonalScheduleBox } from "./boxes/DashboardPersonalScheduleBox.js";
import { DashboardProjectsBox } from "./boxes/DashboardProjectsBox.js";
import { PendingDocSharesBox } from "./boxes/PendingDocSharesBox.js";
import { DashboardNewCompanyModal } from "./modals/DashboardNewCompanyModal.js";
import { DashboardNewUserModal } from "./modals/DashboardNewUserModal.js";
import { getUrlSearchParamsByParamsName } from "../../commons/hooks/useStateWithUrlParams.js";

export type VisibilityKeys =
  | "calendar"
  | "projects"
  | "opportunities"
  | "todos"
  | "schedule";

// need to override topbar theme
const renderMenu = (menu: React.ReactElement): React.ReactElement => (
  <ThemeProvider theme={theme}>{menu}</ThemeProvider>
);

export const Dashboard = () => {
  const { t } = useTranslate(["Dashboard", "Global"]);
  const viewer = useUserData().currentUser!;

  const { dashboardSideTopAd, dashboardLeftTopAd, dashboardRightTopAd } =
    useBranding();

  const { onboardingFlowStage, setOnboardingFlowStage } = useOnboardingFlow();

  const { search } = useLocation();
  const [showNewCompanyPopup, setShowNewCompanyPopup] = React.useState(
    search.includes("newCompany")
  );
  const [showNewUserPopup, setShowNewUserPopup] = React.useState(
    search.includes("newUser")
  );

  const visibilityStore = useVisibilityStore<VisibilityKeys>("Dashboard");
  const { value, saveValue, loading } = visibilityStore;

  const client = useApolloClient();
  const invitationQuery = useInvitationListQuery({
    client,
  });
  const projectContracteeInvitations =
    invitationQuery?.data?.viewer?.projectContracteeInvitations;
  const receivedLinkingInvitations =
    invitationQuery?.data?.viewer?.organisation.receivedLinkingInvitations;

  if (loading) return <SplashScreen />;

  const isCalendarVisible = value.calendar !== false;

  return (
    <Page
      isTopBarVisible
      isBrandingVisible
      title={t("Dashboard")}
      topbarItems={
        <MenuButton buttonProps={{ color: "inherit" }} renderMenu={renderMenu}>
          <ModalOpenButton
            Modal={VisibilityEditModal}
            modalProps={{
              visibilityStore,
              labels: {
                calendar: t("Calendar"),
                projects: t("Projects"),
                opportunities: t("Opportunities"),
                todos: t("To-do list"),
                schedule: t("My Schedule"),
              },
            }}
          >
            <MenuItemWithIcon icon={<ToggleOnIcon />}>
              {t("Configure dashboard")}
            </MenuItemWithIcon>
          </ModalOpenButton>
        </MenuButton>
      }
    >
      <PageContainer>
        <Stack spacing={2} direction="column">
          {/* Visible only in app */}
          <DashboardAppNotificationsBox />

          {/* Invitations */}
          {projectContracteeInvitations &&
            receivedLinkingInvitations &&
            (projectContracteeInvitations.length > 0 ||
              receivedLinkingInvitations.length > 0) && (
              <Box>
                <PageGrid
                  columns={{ xs: 1, lg: 2, xl: isCalendarVisible ? 3 : 4 }}
                >
                  <>
                    <PendingInvitationsBox
                      projectContracteeInvitations={
                        projectContracteeInvitations
                      }
                      receivedLinkingInvitations={receivedLinkingInvitations}
                    />
                  </>
                </PageGrid>
              </Box>
            )}

          <Box>
            <PageGrid columns={{ xs: 1, lg: 2, xl: isCalendarVisible ? 3 : 4 }}>
              <>
                {dashboardLeftTopAd && <BrandingAd ad={dashboardLeftTopAd} />}
                {value.projects !== false && <DashboardProjectsBox />}
              </>
              {value.opportunities !== false &&
              viewer.organisation.isCraftsmanOrganisation ? (
                <RestrictedByOrganisationPermissionWithDebug permission="CREATE_PROJECT">
                  <DashboardOpportunitiesBox />
                </RestrictedByOrganisationPermissionWithDebug>
              ) : null}
              <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_QUOTES">
                <PendingDocSharesBox />
              </RestrictedByOrganisationPermissionWithDebug>
              {value.todos !== false || dashboardRightTopAd ? (
                <>
                  {dashboardRightTopAd && (
                    <BrandingAd ad={dashboardRightTopAd} />
                  )}
                  {value.todos !== false && (
                    <TodosBox
                      seedViewerTeamsFilter
                      renderSeeMoreButton={activeFilters => (
                        <Button
                          component={Link}
                          fullWidth
                          to={{
                            pathname: `/tasks`,
                            search: getUrlSearchParamsByParamsName(
                              { ...activeFilters },
                              "filters"
                            ).toString(),
                          }}
                          color="secondary"
                          variant="text"
                          sx={{
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                          }}
                        >
                          {t("See more", { ns: "Global" })}
                        </Button>
                      )}
                    />
                  )}
                </>
              ) : null}
              {value.schedule !== false &&
              viewer.organisation.capabilities.includes("PLANNING") ? (
                <>
                  <RestrictedByCapabilityWithDebug capability="PLANNING">
                    <DashboardPersonalScheduleBox />
                  </RestrictedByCapabilityWithDebug>
                </>
              ) : null}
            </PageGrid>
          </Box>
        </Stack>
      </PageContainer>

      {((isCalendarVisible &&
        viewer.organisation.capabilities.includes("PLANNING")) ||
        dashboardSideTopAd) && (
        <PageColumn $hasBackground $hasLeftBorder>
          {dashboardSideTopAd && (
            <BrandingAd ad={dashboardSideTopAd} style={{ marginBottom: 12 }} />
          )}
          {isCalendarVisible && (
            <RestrictedByCapabilityWithDebug capability="PLANNING">
              <DashboardPersonalCalendarBox />
            </RestrictedByCapabilityWithDebug>
          )}
        </PageColumn>
      )}

      {showNewCompanyPopup && (
        <DashboardNewCompanyModal
          handleClose={() => setShowNewCompanyPopup(false)}
          organisation={viewer.organisation}
        />
      )}

      {showNewUserPopup && (
        <DashboardNewUserModal handleClose={() => setShowNewUserPopup(false)} />
      )}

      {onboardingFlowStage && (
        <OnboardingFlowModal
          stage={onboardingFlowStage}
          setStage={setOnboardingFlowStage}
        />
      )}
    </Page>
  );
};
